import { useState, useEffect } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { Menu, ChevronDown, User } from "lucide-react";
import { CartButton } from "./CartButton";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { CountrySwitcher } from "./CountrySwitcher";
import { SearchSheet } from "./SearchSheet";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";
import { SHOPIFY_STORE_PERMANENT_DOMAIN } from "@/lib/shopify";

const TIKTOK_URL = "https://www.tiktok.com/@montessori24fi?_r=1&_t=ZN-988IWEQ679T";
const ACCOUNT_URL = `https://${SHOPIFY_STORE_PERMANENT_DOMAIN}/account`;

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.77V1h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

/**
 * Kauppa mega menu — four quiet columns, no coloured tiles, no imagery.
 * Every broad category lives here so the top-level bar can stay short:
 * Kauppa · Pikler · Oppimistornit · IGLU · Montessori-koulu.
 */
const MEGA_COLUMNS = [
  {
    title: "Kiipeily",
    items: [
      { to: "/pikler-kolmio", label: "Pikler-kolmiot" },
      { to: "/kiipeilyteline", label: "Kiipeilytelineet ja kaaret" },
      { to: "/kiipeilyteline-pihalle", label: "Kiipeily pihalle" },
      { to: "/vertailu", label: "Vertaa kiipeilytuotteita" },
    ],
  },
  {
    title: "Oppiminen",
    items: [
      { to: "/oppimistorni", label: "Oppimistornit" },
      { to: "/taaperotorni", label: "Taaperotornit" },
      { to: "/montessori-lelut", label: "Montessori-lelut" },
      { to: "/montessori-koulu", label: "Oppaat ja artikkelit" },
    ],
  },
  {
    title: "Lastenhuone",
    items: [
      { to: "/lastenhuoneen-kalusteet", label: "Kalusteet" },
      { to: "/montessori-sanky", label: "Sängyt ja patjat" },
      { to: "/vaaterekki", label: "Vaaterekit" },
      { to: "/lelulaatikko", label: "Säilytys" },
    ],
  },
  {
    title: "Muut",
    items: [
      { to: "/iglu", label: "IGLU Blocks" },
      { to: "/uutuudet", label: "Uutuudet" },
      { to: "/montessori-vauva", label: "Vauvalle 0–1 v" },
      { to: "/kauppa", label: "Kaikki tuotteet" },
    ],
  },
] as const;

/** Top-level bar — five items, nothing more. */
const PRIMARY_LINKS = [
  { to: "/pikler-kolmio", label: "Pikler" },
  { to: "/oppimistorni", label: "Oppimistornit" },
  { to: "/iglu", label: "IGLU" },
] as const;

const GUIDE_LINKS = [
  { to: "/montessori-koulu", key: "nav.school" },
  { to: "/historia", key: "nav.history" },
  { to: "/koulutus", key: "nav.education" },
  { to: "/meista", key: "nav.about" },
  { to: "/ukk", key: "nav.faq" },
] as const;

const DESKTOP_LINK =
  "nav-underline whitespace-nowrap py-2 text-[15px] text-foreground/75 transition-colors duration-[250ms] hover:text-foreground";
const DESKTOP_LINK_OVER_IMAGE =
  "nav-underline whitespace-nowrap py-2 text-[15px] text-foreground/80 transition-colors duration-[250ms] hover:text-foreground";

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const t = useT();
  const { pathname } = useLocation();

  // Transparent over the homepage hero, solid warm white after scrolling
  // (and always solid on every other route, which has no full-bleed hero).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const transparent = pathname === "/" && !scrolled;
  const desktopLink = transparent ? DESKTOP_LINK_OVER_IMAGE : DESKTOP_LINK;
  const activeLinkClass = "text-foreground";

  return (
    <header
      className={`sticky top-0 z-40 w-full border-b transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 ${
        transparent
          ? "border-transparent bg-transparent"
          : "border-border bg-background/95 shadow-[0_16px_40px_-28px_rgb(47_47_47/0.4)] backdrop-blur-md"
      }`}
    >
      <div className="container-prose relative flex h-16 items-center justify-between md:h-20">
        {/* LEFT — logo */}
        <Link
          to="/"
          className="relative z-10 text-base font-medium uppercase tracking-[0.18em] text-foreground"
        >
          Montessori<span className="text-clay">24</span>
        </Link>

        {/* CENTER — five primary items, absolutely centred */}
        <nav
          aria-label="Päävalikko"
          className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 xl:flex"
        >
          {/* Kauppa — mega menu */}
          <div className="group">
            <Link to="/kauppa" className={`${desktopLink} inline-flex items-center gap-1.5`}>
              {t("nav.shop")}
              <ChevronDown
                className="h-3.5 w-3.5 opacity-50 transition-transform duration-[250ms] group-hover:rotate-180"
                strokeWidth={2}
                aria-hidden
              />
            </Link>
            <div
              className="pointer-events-none invisible absolute left-1/2 top-full z-50 -translate-x-1/2 translate-y-2 pt-4 opacity-0 transition-all duration-[250ms] ease-out group-hover:pointer-events-auto group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100"
              role="menu"
            >
              <div className="grid w-[min(880px,calc(100vw-3rem))] grid-cols-4 gap-x-10 gap-y-8 rounded-[12px] border border-border bg-card p-9 shadow-[0_32px_70px_-30px_rgb(47_47_47/0.35)]">
                {MEGA_COLUMNS.map((col) => (
                  <div key={col.title}>
                    <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                      {col.title}
                    </p>
                    <ul className="mt-4 space-y-2.5">
                      {col.items.map((item) => (
                        <li key={item.to + item.label}>
                          <Link
                            to={item.to}
                            className="text-sm text-foreground/85 transition-colors duration-[250ms] hover:text-clay"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {PRIMARY_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={desktopLink}
              activeProps={{ className: activeLinkClass }}
            >
              {l.label}
            </Link>
          ))}

          <Link to="/montessori-koulu" className={desktopLink} activeProps={{ className: activeLinkClass }}>
            {t("nav.school")}
          </Link>
        </nav>

        {/* RIGHT — search, account, cart (+ mobile menu trigger) */}
        <div className="relative z-10 flex items-center justify-end gap-1 md:gap-2">
          <div className={transparent ? "[&_button]:text-foreground/80" : undefined}>
            <SearchSheet />
          </div>
          <a
            href={ACCOUNT_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t("nav.account")}
            className={`hidden h-11 w-11 items-center justify-center transition-colors duration-[250ms] md:inline-flex ${
              transparent
                ? "text-foreground/80 hover:text-foreground"
                : "text-foreground/75 hover:text-foreground"
            }`}
          >
            <User className="h-5 w-5" strokeWidth={1.75} />
          </a>
          <div className={transparent ? "[&_button]:text-foreground/80" : undefined}>
            <CartButton />
          </div>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={`xl:hidden ${transparent ? "text-foreground hover:bg-foreground/10 hover:text-foreground" : ""}`}
                aria-label={t("nav.openMenu")}
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex w-full max-w-none flex-col gap-0 border-none p-0 sm:max-w-none"
            >
              <SheetHeader className="border-b border-border px-6 py-5">
                <SheetTitle className="text-left text-base font-medium uppercase tracking-[0.18em]">
                  Montessori<span className="text-clay">24</span>
                </SheetTitle>
              </SheetHeader>

              {/* Mobile mirrors the desktop hierarchy: Kauppa groups first,
                  then the same four primary destinations. */}
              <nav aria-label="Mobiilivalikko" className="flex-1 overflow-y-auto px-6 py-4">
                {MEGA_COLUMNS.map((col) => (
                  <details key={col.title} className="group border-b border-border">
                    <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between text-lg text-foreground">
                      {col.title}
                      <ChevronDown
                        className="h-5 w-5 text-muted-foreground transition-transform duration-[250ms] group-open:rotate-180"
                        aria-hidden
                      />
                    </summary>
                    <ul className="pb-4">
                      {col.items.map((item) => (
                        <li key={item.to + item.label}>
                          <Link
                            to={item.to}
                            onClick={() => setOpen(false)}
                            className="flex min-h-11 items-center text-base text-foreground/80"
                          >
                            {item.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </details>
                ))}

                <Link
                  to="/kauppa"
                  onClick={() => setOpen(false)}
                  className="flex min-h-[56px] items-center border-b border-border text-lg text-foreground"
                >
                  {t("nav.all")}
                </Link>

                {/* Guides accordion */}
                <details className="group border-b border-border">
                  <summary className="flex min-h-[56px] cursor-pointer list-none items-center justify-between text-lg text-foreground">
                    {t("nav.guides")}
                    <ChevronDown
                      className="h-5 w-5 text-muted-foreground transition-transform duration-[250ms] group-open:rotate-180"
                      aria-hidden
                    />
                  </summary>
                  <div className="pb-4">
                    {GUIDE_LINKS.map((n) => (
                      <Link
                        key={n.to}
                        to={n.to}
                        onClick={() => setOpen(false)}
                        className="flex min-h-11 items-center text-base text-foreground/80"
                      >
                        {t(n.key)}
                      </Link>
                    ))}
                  </div>
                </details>
              </nav>

              {/* Menu footer — market, language, social */}
              <div className="border-t border-border px-6 py-5">
                <div className="flex flex-wrap items-center gap-3">
                  <CountrySwitcher />
                  <LanguageSwitcher />
                </div>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex min-h-11 items-center gap-2 text-sm text-muted-foreground transition-colors hover:text-foreground"
                >
                  <TikTokIcon className="h-[18px] w-[18px]" />
                  TikTok
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
